// stripeTerminal.js
import { getConnectionToken, getPaymentIntent } from "../services/api";
import { useRef } from 'react';
import config from "../utils/config";
let terminal;
let connectedReader = null;
let commandInProgress = false;

// Initialize Stripe Terminal once
export async function initTerminal(accessToken) {
  const proxyString = config.get("services").PROXY_STRING;
  const tokenEndpoint = config.get("services").CONNECTIONTOKEN_API_STRING;
  const apiUrl = `${proxyString}${tokenEndpoint}`;
  const sourceHeader = config.get("services").API_SOURCE_HEADER;

  if (!window.StripeTerminal) {
    console.error("StripeTerminal SDK not loaded");
    return;
  }

  terminal = window.StripeTerminal.create({
    onFetchConnectionToken: async () => {
      const token = await getConnectionToken(accessToken, apiUrl, sourceHeader); // Call your backend to get a connection token
      return token;
    },
    onUnexpectedReaderDisconnect: () => {
      console.error("Reader disconnected unexpectedly");
      connectedReader = null;
    },
  });
  return terminal;
}
export function getTerminal() {
  return terminal;
};
// Discover and connect to reader
export async function connectToReader(options = {}) {
  const { simulated = simulated, locationId = locationId } = options;
  console.log("Discovering and connecting to reader...");
  if (!terminal) {
    throw new Error("Terminal not initialized. Call initTerminal() first.");
  }
  // If already connected, return the existing reader
  if (connectedReader) {
    console.log("Already connected to reader");
    return connectedReader;
  }
  try {
    // Discover readers
    const discoverConfig = { simulated };
    if (!simulated && locationId) {
      discoverConfig.location = locationId;
    }
    const discoverResult = await terminal.discoverReaders(discoverConfig);
    if (discoverResult.error) throw discoverResult.error;
    console.log("Readers discovered");
    if (discoverResult.discoveredReaders.length === 0) {
      throw new Error(
        simulated
          ? "No simulated readers found"
          : "No readers found. Make sure your Reader is powered on and registered."
      );
    }
    const reader = discoverResult.discoveredReaders[0];
    // console.log("Connecting to reader:", reader.label || reader.serial_number);
    console.log("Connecting to reader");

    // Connect to reader
    const connectResult = await terminal.connectReader(reader);
    if (connectResult.error) throw connectResult.error;
    connectedReader = connectResult.reader;
    console.log("Connected to reader");
    return connectedReader;
  } catch (err) {
    console.error("Failed to connect to reader:", err);
    return err;
  }
}
// payment flow
export async function InitiatePayment(accessToken, options = {}, callbacks = {}) {

  // get reader settings
  const readerSettings = config.get("ReaderSettings").find(
    r => r.restaurant_id === String(options.restaurantId)
  );
  const reader_config = {
    simulated: readerSettings?.reader_mode === "simulated",
    locationId: readerSettings?.locationId
  };

  const proxyString = config.get("services").PROXY_STRING;
  const paymentIntentEndpoint = config.get("services").PAYMENTINTENT_API_STRING;
  const apiUrl = `${proxyString}${paymentIntentEndpoint}`;
  const sourceHeader = config.get("services").API_SOURCE_HEADER;
  if (commandInProgress) {
    console.warn("Command already running");
    return;
  }
  commandInProgress = true;
  const {
    //simulated = false,
    //locationId = null,
    amount = 1000,
    restaurantId = null
  } = options;

  // callbacks to show appropriate UI states
  const {
    onWaitingForCard = () => { },      // Called when waiting for card tap
    onCardTapped = () => { },          // Called when card is tapped
    onProcessing = () => { },          // Called when processing payment
    onSuccess = () => { },             // Called when payment succeeds
    onError = () => { }                // Called when payment fails
  } = callbacks;

  if (!terminal) {
    try {
      await initTerminal(accessToken);
    }
    catch {
      console.error("Terminal not initialized. Call initTerminal() first.");
      onError(new Error("Terminal not initialized"));
      return;
    }
  }
  //Check if reader is connected
  // if (!connectedReader) {
  //   const error = new Error("No reader connected. Call connectToReader() first.");
  //   console.log("READER ERROR", error);
  //   onError(error);
  //   throw error;
  // }
  if (!connectedReader) {
    console.log("Reader not connected. Reconnecting...");
    await connectToReader(reader_config);
  }
  // actual payment flow starts here
  try {
    // 1. Create PaymentIntent on backend
    const payload = { amount: amount, restaurant_id: restaurantId };
    // Call your backend to get a payment intent
    const paymentIntentResult = await getPaymentIntent(payload, accessToken, apiUrl, sourceHeader);
    const client_secret = paymentIntentResult.client_secret;
    if (!client_secret) {
      throw new Error("No client_secret received from backend");
    }

    console.log("PaymentIntent created with client_secret");

    // CALLBACK: Now waiting for card tap - show "waiting for card tapping" text
    onWaitingForCard();

    console.log("Waiting for customer to present card...");
    console.log("Please tap/insert card on the WisePOS E reader");

    //check connection status before collecting payment
    if (!connectedReader) {
      console.log("Reader not connected. Reconnecting...");
      await connectToReader(reader_config);
    }
    // collect payment method
    const collectResult = await terminal.collectPaymentMethod(client_secret);
    if (collectResult.error) {
      console.log("Error collecting payment", collectResult.error);
      //throw collectResult.error;
      onError(collectResult.error);
    }
    else {
      console.log("Card tapped successfully");
      onCardTapped(collectResult.paymentIntent);
      console.log("Processing payment...");
      // CALLBACK: Now processing payment - show loader
      onProcessing();
      // 3. Process payment
      const processResult = await terminal.processPayment(collectResult.paymentIntent);
      if (processResult.error) {
        //throw processResult.error;
        onError(processResult.error);
      }
      else {
        console.log("Payment successful");
        // CALLBACK: Payment succeeded
        onSuccess(processResult.paymentIntent);
        return processResult.paymentIntent;
      }
    }

  } catch (err) {
    console.error("Payment failed:", err);
    // CALLBACK: cancel payment
    try {
      cancelPaymentIfNeeded()
    } catch (cancelErr) {
      console.warn("Cancel failed:", cancelErr);
    }
    onError(err);
  }
  finally {
    commandInProgress = false; // this is to avoid react reender issues
  }
};
// cancel payment function 
export async function cancelPaymentIfNeeded(statusWatcherRef) {
  console.log("CANCELLING");

  try {

    const status = terminal.getPaymentStatus();
    console.log("STATUS", status);
    //Stop the watcher
    if (statusWatcherRef.current) {
      clearInterval(statusWatcherRef.current);
      statusWatcherRef.current = null;
    }

    // Only cancel if payment is active
    if (status === "waiting_for_input") {
      console.log("Cancelling in-progress payment...");
      await terminal.cancelCollectPaymentMethod();
    }
    if (status === "processing") {
      console.log("Cancelling in-progress payment...");
      await terminal.cancelProcessPayment();
    }
    commandInProgress = false;
    return;
  } catch (err) {
    console.error("Error cancelling payment:", err);
    commandInProgress = false;
  }
}

